import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useWorkspace from "@/hooks/useWorkspace";
import useDocumentLink from "@/hooks/useDocumentLink";
import AppRoutes from "@/constants/routes/AppRoutes";
import PageContent from "../PageContent";
import {
  WorkspaceDocument,
  WorkspaceDocumentType,
  getAllWorkspaceDocuments,
} from "@/models/workspace/Workspace";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { parseDocumentViewerHighlightParams } from "@/partials/document-renderer/utils/referenceHighlightQuery";

/** Aligns blob / document ids from URL, API, and search (case and hyphen variants). */
function normalizeWorkspaceBlobKey(value: string | undefined): string {
  if (!value) return "";
  return value.trim().replace(/-/g, "").toLowerCase();
}

const WorkspaceDocumentRedirectView = () => {
  const { workspaceId, documentId } = useParams<{
    workspaceId: string;
    documentId: string;
  }>();

  const [searchParams] = useSearchParams();

  const {
    highlightIds: htmlIds,
    refMatchText,
    refChunkText,
    docSearchTerm,
  } = useMemo(
    () => parseDocumentViewerHighlightParams(searchParams),
    [searchParams]
  );
  const navigate = useNavigate();
  const { useQuerySingle, useGetDocumentHtml } = useWorkspace();
  const {
    setWorkspaceDocument,
    setWorkspace,
    setSelectedVersion,
    selectedVersion,
  } = useWorkspaceDocumentVersionStore();

  const [document, setDocument] = useState<WorkspaceDocument | null>(null);
  const [isCheckingHtml, setIsCheckingHtml] = useState(false);

  const fetchedWorkspace = useQuerySingle(workspaceId);
  // Use selectedVersion's blobName if available, otherwise fall back to document's blobName
  const blobNameToFetch =
    selectedVersion?.blobName ?? document?.blobName ?? undefined;
  const revisionVersionToFetch =
    selectedVersion?.revisionVersion ?? document?.activeRevision;
  const fetchedDocumentHtml = useGetDocumentHtml(
    workspaceId,
    document?.id,
    blobNameToFetch,
    revisionVersionToFetch
  );
  // The viewer's document-links query (same key), started with the HTML: the
  // viewer renders the moment it mounts with this HTML cached, which held its
  // own early start back until after the whole first parse (LAR-487).
  const { useQueryByDocumentId: useQueryDocumentLinks } = useDocumentLink();
  useQueryDocumentLinks(document ? (blobNameToFetch ?? "") : "");

  // First, find the document in the workspace
  useEffect(() => {
    if (fetchedWorkspace?.isFetched && fetchedWorkspace?.data && documentId) {
      const workspace = fetchedWorkspace.data;
      const allDocuments = getAllWorkspaceDocuments(workspace);

      // Find document by ID or blobName
      let foundDocument: WorkspaceDocument | undefined;
      let matchingRevision: {
        blobId: string;
        size: number;
        updatedAt: Date;
        revisionVersion: number;
      } | null = null;

      const routeKey = normalizeWorkspaceBlobKey(documentId);
      for (const doc of allDocuments) {
        if (
          normalizeWorkspaceBlobKey(doc.id) === routeKey ||
          normalizeWorkspaceBlobKey(doc.blobName) === routeKey
        ) {
          foundDocument = doc;
          break;
        }
        const revision = doc.revisions.find(
          (rev) => normalizeWorkspaceBlobKey(rev.blobId) === routeKey
        );
        if (revision) {
          foundDocument = doc;
          matchingRevision = {
            blobId: revision.blobId,
            size: revision.size,
            updatedAt: revision.updatedAt,
            revisionVersion: revision.revisionVersion,
          };
          break;
        }
      }

      if (!foundDocument) {
        navigate(AppRoutes.workspaceFileNotFound, { replace: true });
        return;
      }
      if (!workspace) {
        navigate(AppRoutes.workspaceNotFound, { replace: true });
        return;
      }
      setDocument(foundDocument);
      setWorkspaceDocument(foundDocument);
      setWorkspace(workspace);

      // If we found a matching revision, use its data; otherwise use document's default data
      if (matchingRevision) {
        setSelectedVersion({
          blobName: matchingRevision.blobId,
          name: foundDocument.name,
          updatedAt: matchingRevision.updatedAt,
          revisionVersion: matchingRevision.revisionVersion,
          size: matchingRevision.size,
          documentType: foundDocument.documentType,
        });
      } else {
        setSelectedVersion({
          blobName: foundDocument.blobName,
          name: foundDocument.name,
          updatedAt: foundDocument.updatedAt
            ? new Date(foundDocument.updatedAt)
            : new Date(),
          revisionVersion: foundDocument.activeRevision,
          size: foundDocument.size,
          documentType: foundDocument.documentType,
        });
      }
      setIsCheckingHtml(true);
    }
    if (fetchedWorkspace.error && fetchedWorkspace.isFetched) {
      navigate(AppRoutes.workspaceNotFound, { replace: true });
      return;
    }
  }, [
    fetchedWorkspace.error,
    fetchedWorkspace.isFetched,
    fetchedWorkspace.data,
    documentId,
    navigate,
    setWorkspaceDocument,
    setWorkspace,
    setSelectedVersion,
  ]);

  // Once we have the document, check for HTML and redirect
  useEffect(() => {
    if (!document || !isCheckingHtml || !workspaceId) return;

    // Wait for HTML check to complete
    if (fetchedDocumentHtml.isFetching) return;

    if (fetchedDocumentHtml.isFetched) {
      // Use selectedVersion's blobName if available, otherwise use document's blobName
      const blobNameToUse = selectedVersion?.blobName ?? document.blobName;

      // If HTML is available, redirect to HTML viewer
      if (fetchedDocumentHtml.data) {
        navigate(
          AppRoutes.workspaceDocumentHTML(workspaceId, blobNameToUse, htmlIds, {
            matchText: refMatchText,
            chunkText: refChunkText,
            docSearch: docSearchTerm,
          }),
          { replace: true }
        );
        return;
      }

      // No HTML available, redirect to appropriate viewer based on document type
      // Use the actual document type if it's valid, otherwise fall back to OTHER
      const validDocumentTypes = Object.values(WorkspaceDocumentType);
      const documentType = validDocumentTypes.includes(document.documentType)
        ? document.documentType
        : WorkspaceDocumentType.OTHER;

      const type = documentType.toLowerCase();

      // Only update selectedVersion if it's not already set (to preserve revision data)
      if (!selectedVersion) {
        setWorkspaceDocument(document);
        setSelectedVersion({
          blobName: document.blobName,
          name: document.name,
          updatedAt: document.updatedAt
            ? new Date(document.updatedAt)
            : new Date(),
          revisionVersion: document.activeRevision,
          size: document.size,
          documentType: document.documentType,
        });
      }
      navigate(AppRoutes.workspaceDocumentPDF(workspaceId, blobNameToUse, type), {
        replace: true,
      });
    }
  }, [
    document,
    isCheckingHtml,
    workspaceId,
    fetchedDocumentHtml.isFetching,
    fetchedDocumentHtml.isFetched,
    fetchedDocumentHtml.data,
    navigate,
    setWorkspaceDocument,
    setWorkspace,
    setSelectedVersion,
    selectedVersion,
    htmlIds,
    refMatchText,
    refChunkText,
    docSearchTerm,
  ]);

  // Show loading state that matches the document viewer
  return (
    <PageContent title="Indlæser dokument...">
      <div className="h-[calc(100vh-3rem)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 mt-12">
          <Spinner className="size-12" />
          <p className="text-sm text-muted-foreground">Henter dokument...</p>
        </div>
      </div>
    </PageContent>
  );
};

export default WorkspaceDocumentRedirectView;
